import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage, useAuth } from '@/lib/context';
import { useProAuth } from '@/lib/ProAuthContext';
import BudgetSection, { BudgetSummary } from '@/components/BudgetSection';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Search, MapPin, Clock, CheckCircle, ArrowRight, 
  Loader2, Send, User, Mail, Phone, Home,
  Sparkles, Shield, Users, Zap, Truck, MessageSquare,
  AlertCircle, Crown, X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category IDs (translations come from i18n)
const CATEGORY_IDS = [
  "construction", "electrical", "plumbing", "hvac", "cleaning",
  "moving", "renovation", "painting", "carpentry", "landscaping",
  "welding", "roofing", "flooring", "auto_repair", "it_services",
  "security", "other"
];

// Priority categories (get fast response badge)
const PRIORITY_CATEGORIES = ["moving", "transport"];

// Country codes (translations come from i18n)
const COUNTRY_CODES = ["EE", "FI", "SE", "NO", "DE", "DK", "LV", "LT", "PL"];

// ============================================================
// PRO UPGRADE MODAL
// ============================================================

const ProUpgradeModal = ({ open, onClose, t }) => {
  const navigate = useNavigate();
  
  const benefits = [
    t('serviceMvp.quickResponse.upgradeModal.benefit1') || "Instant SMS notifications to providers",
    t('serviceMvp.quickResponse.upgradeModal.benefit2') || "Priority placement in provider queues",
    t('serviceMvp.quickResponse.upgradeModal.benefit3') || "Responses in 10-15 minutes",
    t('serviceMvp.quickResponse.upgradeModal.benefit4') || "Included in PRO subscription"
  ];

  const handleUpgradeClick = () => {
    onClose();
    navigate('/pro/login');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="pro-upgrade-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-yellow-500" />
            {t('serviceMvp.quickResponse.upgradeModal.title')}
          </DialogTitle>
          <DialogDescription>
            {t('serviceMvp.quickResponse.upgradeModal.description')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-lg p-4">
            <ul className="space-y-2">
              {benefits.map((benefit, idx) => (
                <li key={idx} className="flex items-center gap-2 text-sm">
                  <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                  <span>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="text-center">
            <span className="text-2xl font-bold text-orange-600">
              {t('serviceMvp.quickResponse.upgradeModal.price')}
            </span>
          </div>
          <div className="flex flex-col gap-2">
            <Button 
              className="w-full bg-gradient-to-r from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600"
              onClick={handleUpgradeClick}
              data-testid="upgrade-pro-btn"
            >
              <Crown className="h-4 w-4 mr-2" />
              {t('serviceMvp.quickResponse.upgradeModal.cta')}
            </Button>
            <Button variant="ghost" onClick={onClose} className="w-full">
              {t('serviceMvp.quickResponse.upgradeModal.later')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================================
// PUBLIC SERVICE REQUEST FORM
// ============================================================

export const ServiceRequestForm = ({ onSuccess }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { isPro, isAuthenticated } = useProAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showProModal, setShowProModal] = useState(false);
  const [formData, setFormData] = useState({
    category: '',
    category_custom: '',
    description: '',
    country: '',
    city: '',
    contact_email: '',
    contact_name: '',
    contact_phone: '',
    quick_response: false,  // PRO feature
    budget: null  // BudgetSection initialises this
  });

  // Use real PRO status from ProAuthContext
  const userIsPro = isPro;

  const handleQuickResponseToggle = (checked) => {
    if (!userIsPro && checked) {
      // Show upgrade modal for non-PRO users
      setShowProModal(true);
    } else {
      setFormData({...formData, quick_response: checked});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.category || !formData.description || !formData.country || !formData.city || !formData.contact_email) {
      toast.error(t('serviceMvp.form.fillRequired'));
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/service-mvp/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || t('serviceMvp.errors.submitFailed'));
      }

      const data = await response.json();
      toast.success(t('serviceMvp.form.submitSuccess'));
      
      if (onSuccess) {
        onSuccess(data);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl mx-auto border-2 shadow-lg" data-testid="service-request-form">
      <CardHeader className="bg-gradient-to-r from-orange-50 to-amber-50 border-b">
        <CardTitle className="text-2xl flex items-center gap-2">
          <Send className="h-6 w-6 text-orange-500" />
          {t('serviceMvp.form.title')}
        </CardTitle>
        <CardDescription>
          {t('serviceMvp.form.subtitle')}
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Category */}
          <div className="space-y-2">
            <Label htmlFor="category">{t('serviceMvp.form.category')} *</Label>
            <Select 
              value={formData.category} 
              onValueChange={(value) => setFormData({...formData, category: value})}
            >
              <SelectTrigger data-testid="category-select">
                <SelectValue placeholder={t('serviceMvp.form.categoryPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {CATEGORY_IDS.map((catId) => (
                  <SelectItem key={catId} value={catId}>
                    <div className="flex items-center gap-2">
                      {catId === 'moving' && <Truck className="h-4 w-4 text-orange-500" />}
                      {t(`serviceMvp.categories.${catId}`)}
                      {PRIORITY_CATEGORIES.includes(catId) && (
                        <Badge variant="secondary" className="ml-2 bg-orange-100 text-orange-700 text-xs px-1.5 py-0">
                          <Zap className="h-3 w-3 mr-0.5" />
                          {t('serviceMvp.categoryBadges.fastResponse')}
                        </Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Transport priority note */}
            {PRIORITY_CATEGORIES.includes(formData.category) && (
              <div className="flex items-start gap-2 p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm text-orange-800">
                <Zap className="h-4 w-4 text-orange-500 mt-0.5 flex-shrink-0" />
                <span>{t('serviceMvp.categoryBadges.transportNote')}</span>
              </div>
            )}
            
            {formData.category === 'other' && (
              <Input
                placeholder={t('serviceMvp.form.customCategory')}
                value={formData.category_custom}
                onChange={(e) => setFormData({...formData, category_custom: e.target.value})}
                className="mt-2"
                data-testid="custom-category-input"
              />
            )}
          </div>

          {/* Location */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="country">{t('serviceMvp.form.country')} *</Label>
              <Select 
                value={formData.country} 
                onValueChange={(value) => setFormData({...formData, country: value})}
              >
                <SelectTrigger data-testid="country-select">
                  <SelectValue placeholder={t('serviceMvp.form.selectCountry')} />
                </SelectTrigger>
                <SelectContent>
                  {COUNTRY_CODES.map((code) => (
                    <SelectItem key={code} value={t(`serviceMvp.countries.${code}`)}>
                      {t(`serviceMvp.countries.${code}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="city">{t('serviceMvp.form.city')} *</Label>
              <Input
                placeholder={t('serviceMvp.form.cityPlaceholder')}
                value={formData.city}
                onChange={(e) => setFormData({...formData, city: e.target.value})}
                data-testid="city-input"
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">{t('serviceMvp.form.description')} *</Label>
            <Textarea
              placeholder={t('serviceMvp.form.descriptionPlaceholder')}
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              rows={4}
              className="resize-none"
              data-testid="description-textarea"
            />
            <p className="text-xs text-muted-foreground">
              {t('serviceMvp.form.descriptionHint')}
            </p>
          </div>

          {/* Universal Budget */}
          <BudgetSection
            category={formData.category}
            country={formData.country}
            value={formData.budget}
            onChange={(b) => setFormData({...formData, budget: b})}
          />

          {/* Quick Response Toggle (PRO Feature) */}
          <div className="p-4 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${formData.quick_response ? 'bg-orange-500' : 'bg-gray-200'}`}>
                  <AlertCircle className={`h-5 w-5 ${formData.quick_response ? 'text-white' : 'text-gray-500'}`} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{t('serviceMvp.quickResponse.toggle')}</span>
                    <Badge variant="secondary" className="bg-yellow-100 text-yellow-700 text-xs">
                      <Crown className="h-3 w-3 mr-0.5" />
                      {t('serviceMvp.quickResponse.badge')}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {t('serviceMvp.quickResponse.description')}
                  </p>
                </div>
              </div>
              <Switch
                checked={formData.quick_response}
                onCheckedChange={handleQuickResponseToggle}
                disabled={!userIsPro && formData.quick_response}
                data-testid="quick-response-toggle"
              />
            </div>
            {formData.quick_response && (
              <div className="mt-3 flex items-center gap-2 text-sm text-orange-700">
                <Zap className="h-4 w-4" />
                {t('serviceMvp.quickResponse.enabled')}
              </div>
            )}
            {!userIsPro && !formData.quick_response && (
              <button 
                type="button"
                onClick={() => setShowProModal(true)}
                className="mt-2 text-sm text-orange-600 hover:text-orange-700 underline"
              >
                {t('serviceMvp.quickResponse.upgrade')}
              </button>
            )}
          </div>

          {/* Contact Info */}
          <div className="space-y-4 pt-4 border-t">
            <h3 className="font-medium flex items-center gap-2">
              <User className="h-4 w-4" />
              {t('serviceMvp.form.contactInfo')}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-1">
                  <Mail className="h-3 w-3" />
                  {t('serviceMvp.form.email')} *
                </Label>
                <Input
                  type="email"
                  placeholder="your@email.com"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                  data-testid="email-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="name">{t('serviceMvp.form.name')} ({t('serviceMvp.form.optional')})</Label>
                <Input
                  placeholder={t('serviceMvp.form.namePlaceholder')}
                  value={formData.contact_name}
                  onChange={(e) => setFormData({...formData, contact_name: e.target.value})}
                  data-testid="name-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-1">
                <Phone className="h-3 w-3" />
                {t('serviceMvp.form.phone')} ({t('serviceMvp.form.optional')})
              </Label>
              <Input
                type="tel"
                placeholder="+358 40 123 4567"
                value={formData.contact_phone}
                onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                data-testid="phone-input"
              />
            </div>
          </div>

          {/* Submit */}
          <Button 
            type="submit" 
            className="w-full bg-orange-500 hover:bg-orange-600 text-lg py-6"
            disabled={loading}
            data-testid="submit-request-btn"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                {t('serviceMvp.form.submitting')}
              </>
            ) : (
              <>
                <Send className="mr-2 h-5 w-5" />
                {t('serviceMvp.form.submit')}
              </>
            )}
          </Button>

          {/* Trust indicators */}
          <div className="flex flex-wrap justify-center gap-4 text-xs text-muted-foreground pt-2">
            <span className="flex items-center gap-1">
              <Shield className="h-3 w-3 text-green-500" />
              {t('serviceMvp.trust.noSpam')}
            </span>
            <span className="flex items-center gap-1">
              <Sparkles className="h-3 w-3 text-orange-500" />
              {t('serviceMvp.trust.aiMatching')}
            </span>
            <span className="flex items-center gap-1">
              <Users className="h-3 w-3 text-blue-500" />
              {t('serviceMvp.trust.verifiedProviders')}
            </span>
          </div>
        </form>
      </CardContent>

      {/* PRO Upgrade Modal */}
      <ProUpgradeModal 
        open={showProModal} 
        onClose={() => setShowProModal(false)} 
        t={t} 
      />
    </Card>
  );
};

// ============================================================
// SUCCESS VIEW (After submission)
// ============================================================

export const ServiceRequestSuccess = ({ request }) => {
  const { t } = useLanguage();
  const [notificationStatus, setNotificationStatus] = useState(null);
  const [polling, setPolling] = useState(true);
  
  const isPriority = request.is_priority || PRIORITY_CATEGORIES.includes(request.category?.toLowerCase());
  const isQuickResponse = request.quick_response || notificationStatus?.quick_response;

  // Poll for notification status updates
  useEffect(() => {
    if (!polling) return;
    
    const pollStatus = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/service-mvp/requests/${request.id}/status?token=${request.access_token}`
        );
        if (response.ok) {
          const data = await response.json();
          setNotificationStatus(data);
          
          // Stop polling once we have notification counts
          if (data.notified_email_count > 0 || data.notified_sms_count > 0) {
            setPolling(false);
          }
        }
      } catch (error) {
        console.error('Failed to poll status:', error);
      }
    };

    // Initial poll
    pollStatus();
    
    // Poll every 2 seconds for up to 30 seconds
    const interval = setInterval(pollStatus, 2000);
    const timeout = setTimeout(() => {
      setPolling(false);
      clearInterval(interval);
    }, 30000);

    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [request.id, request.access_token, polling]);

  const totalNotified = (notificationStatus?.notified_sms_count || 0) + (notificationStatus?.notified_email_count || 0);

  return (
    <Card className="w-full max-w-2xl mx-auto border-2 border-green-200 shadow-lg" data-testid="request-success">
      <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b text-center">
        <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
          <CheckCircle className="h-8 w-8 text-green-600" />
        </div>
        <CardTitle className="text-2xl text-green-800">
          {t('serviceMvp.success.title')}
        </CardTitle>
        <CardDescription className="text-green-700">
          {isQuickResponse 
            ? t('serviceMvp.quickResponse.successNote')
            : isPriority 
              ? t('serviceMvp.success.subtitlePriority') 
              : t('serviceMvp.success.subtitle')
          }
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        {/* Quick Response Badge */}
        {isQuickResponse && (
          <div className="bg-gradient-to-r from-orange-100 to-yellow-100 border border-orange-300 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 text-orange-700 font-medium">
              <Crown className="h-5 w-5 text-yellow-600" />
              <span>Quick Response Active</span>
              <Badge className="bg-yellow-200 text-yellow-800 text-xs">PRO</Badge>
            </div>
            <p className="text-sm text-orange-600 mt-1">
              Providers notified urgently via SMS. Expect replies in 10-15 minutes.
            </p>
          </div>
        )}

        {/* Notification Status */}
        {(isPriority || isQuickResponse) && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
            {polling ? (
              <div className="flex items-center justify-center gap-2 text-orange-700">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>{t('serviceMvp.success.notifying')}</span>
              </div>
            ) : totalNotified > 0 ? (
              <div className="space-y-2">
                <div className="flex items-center justify-center gap-2 text-green-700 font-medium">
                  <CheckCircle className="h-5 w-5" />
                  <span>{t('serviceMvp.success.notifiedProviders', { count: totalNotified })}</span>
                </div>
                <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
                  {notificationStatus?.notified_sms_count > 0 && (
                    <span className="flex items-center gap-1">
                      <MessageSquare className="h-4 w-4" />
                      {notificationStatus.notified_sms_count} SMS
                    </span>
                  )}
                  {notificationStatus?.notified_email_count > 0 && (
                    <span className="flex items-center gap-1">
                      <Mail className="h-4 w-4" />
                      {notificationStatus.notified_email_count} Email
                    </span>
                  )}
                </div>
                <p className="text-sm text-orange-700 mt-2">
                  <Zap className="h-4 w-4 inline mr-1" />
                  {isQuickResponse 
                    ? "Expect replies in 10-15 minutes!" 
                    : t('serviceMvp.success.fastResponseHint')
                  }
                </p>
              </div>
            ) : (
              <div className="text-orange-700">
                <Sparkles className="h-5 w-5 inline mr-2" />
                {t('serviceMvp.success.matching')}
              </div>
            )}
          </div>
        )}

        <div className="bg-slate-50 rounded-lg p-4 space-y-2">
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('serviceMvp.success.category')}:</span>
            <span className="font-medium flex items-center gap-2">
              {request.category}
              {isQuickResponse ? (
                <Badge className="bg-yellow-100 text-yellow-700 text-xs">
                  <Crown className="h-3 w-3 mr-0.5" />
                  Quick Response
                </Badge>
              ) : isPriority && (
                <Badge className="bg-orange-100 text-orange-700 text-xs">
                  <Zap className="h-3 w-3 mr-0.5" />
                  {t('serviceMvp.categoryBadges.fastResponse')}
                </Badge>
              )}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('serviceMvp.success.location')}:</span>
            <span className="font-medium">{request.city}, {request.country}</span>
          </div>
          {request.budget && (
            <div className="flex justify-between" data-testid="success-budget-row">
              <span className="text-muted-foreground">{t('budget.summary.label')}:</span>
              <span className="font-medium"><BudgetSummary value={request.budget} /></span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-muted-foreground">{t('serviceMvp.success.status')}:</span>
            <Badge variant="secondary" className="bg-green-100 text-green-700">
              {polling ? t('serviceMvp.success.notifying') : t('serviceMvp.success.matching')}
            </Badge>
          </div>
        </div>

        <div className="text-center space-y-2">
          <p className="text-sm text-muted-foreground">
            {t('serviceMvp.success.checkEmail')}
          </p>
          <p className="text-sm text-muted-foreground">
            {t('serviceMvp.success.bookmarkLink')}
          </p>
          <code className="block bg-slate-100 px-3 py-2 rounded text-xs break-all">
            {`${window.location.origin}/service-request/${request.id}?token=${request.access_token}`}
          </code>
        </div>

        <div className="flex flex-col gap-3 pt-4">
          <Link to={`/service-request/${request.id}?token=${request.access_token}`}>
            <Button className="w-full" data-testid="view-request-btn">
              {t('serviceMvp.success.viewRequest')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <Link to="/">
            <Button variant="outline" className="w-full" data-testid="back-home-btn">
              <Home className="mr-2 h-4 w-4" />
              {t('serviceMvp.nav.home')}
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
};

// ============================================================
// SERVICE REQUEST DETAIL PAGE
// ============================================================

export const ServiceRequestDetailPage = () => {
  const { t } = useLanguage();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRequest = async () => {
      try {
        const url = token 
          ? `${API_URL}/api/service-mvp/requests/${id}?token=${token}`
          : `${API_URL}/api/service-mvp/requests/${id}`;
        
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(t('serviceMvp.errors.notFound'));
        }
        
        const result = await response.json();
        setData(result);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRequest();
  }, [id, token, t]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <Link to="/">
              <Button>
                <Home className="mr-2 h-4 w-4" />
                {t('serviceMvp.nav.home')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const request = data?.request;
  const responses = data?.responses || [];
  const isRequester = data?.is_requester;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-orange-500" data-testid="logo">
            KEIKKA
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm">
              <Home className="mr-2 h-4 w-4" />
              {t('serviceMvp.nav.home')}
            </Button>
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Request Details */}
        <Card className="mb-8" data-testid="request-detail-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <Badge variant="secondary" className="bg-orange-100 text-orange-700">
                {request.category}
              </Badge>
              <Badge variant={request.status === 'open' ? 'default' : 'secondary'}>
                {request.status}
              </Badge>
            </div>
            <CardTitle className="text-xl mt-2">
              {t('serviceMvp.detail.serviceRequest')}
            </CardTitle>
            <CardDescription className="flex items-center gap-2">
              <MapPin className="h-4 w-4" />
              {request.city}, {request.country}
              <span className="mx-2">•</span>
              <Clock className="h-4 w-4" />
              {new Date(request.created_at).toLocaleDateString()}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="whitespace-pre-wrap">{request.description}</p>
            </div>
          </CardContent>
        </Card>

        {/* Responses Section */}
        {isRequester && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Users className="h-5 w-5" />
              {t('serviceMvp.detail.responses')} ({responses.length})
            </h2>

            {responses.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-orange-500" />
                  <p>{t('serviceMvp.detail.waitingResponses')}</p>
                  <p className="text-sm mt-2">
                    {t('serviceMvp.detail.emailNotification')}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {responses.map((resp) => (
                  <Card key={resp.id} className="border-l-4 border-l-green-500">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">{resp.provider_name}</CardTitle>
                        {resp.estimated_price && (
                          <Badge className="bg-green-100 text-green-700">
                            {resp.estimated_price} {resp.currency}
                          </Badge>
                        )}
                      </div>
                      {resp.availability && (
                        <CardDescription className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {resp.availability}
                        </CardDescription>
                      )}
                    </CardHeader>
                    <CardContent>
                      <p className="whitespace-pre-wrap">{resp.message}</p>
                      
                      {/* Provider contact */}
                      <div className="mt-4 pt-4 border-t flex flex-wrap gap-4 text-sm">
                        {resp.provider_email && (
                          <a 
                            href={`mailto:${resp.provider_email}`}
                            className="flex items-center gap-1 text-blue-600 hover:underline"
                          >
                            <Mail className="h-4 w-4" />
                            {resp.provider_email}
                          </a>
                        )}
                        {resp.provider_phone && (
                          <a 
                            href={`tel:${resp.provider_phone}`}
                            className="flex items-center gap-1 text-blue-600 hover:underline"
                          >
                            <Phone className="h-4 w-4" />
                            {resp.provider_phone}
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Provider View (no token) */}
        {!isRequester && (
          <Card>
            <CardContent className="py-8 text-center">
              <p className="text-muted-foreground">
                {t('serviceMvp.detail.providerNote')}
              </p>
              <p className="text-sm mt-2">
                {t('serviceMvp.detail.loginToRespond')}
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

// ============================================================
// BROWSE REQUESTS PAGE (For Providers)
// ============================================================

export const BrowseRequestsPage = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [requests, setRequests] = useState([]);
  const [filters, setFilters] = useState({
    country: '',
    category: ''
  });

  useEffect(() => {
    const fetchRequests = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.country) params.append('country', filters.country);
        if (filters.category) params.append('category', filters.category);
        
        const response = await fetch(`${API_URL}/api/service-mvp/requests?${params}`);
        const data = await response.json();
        setRequests(data.requests || []);
      } catch (error) {
        toast.error(t('serviceMvp.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    fetchRequests();
  }, [filters, t]);

  // Get translated category name
  const getCategoryName = (catId) => {
    if (!catId) return '';
    const translated = t(`serviceMvp.categories.${catId}`);
    // If translation returns the key itself, or the missing-marker, return the original id
    if (typeof translated !== 'string') return catId;
    if (translated.startsWith('serviceMvp.categories.')) return catId;
    if (translated.startsWith('[MISSING:')) return catId;
    return translated;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="text-2xl font-bold text-orange-500" data-testid="logo">
            KEIKKA
          </Link>
          <Link to="/">
            <Button variant="ghost" size="sm">
              <Home className="mr-2 h-4 w-4" />
              {t('serviceMvp.nav.home')}
            </Button>
          </Link>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">
            {t('serviceMvp.browse.title')}
          </h1>
          <p className="text-muted-foreground">
            {t('serviceMvp.browse.subtitle')}
          </p>
        </div>

        {/* Filters */}
        <div className="flex gap-4 mb-6">
          <Select 
            value={filters.country || "all"} 
            onValueChange={(value) => setFilters({...filters, country: value === "all" ? "" : value})}
          >
            <SelectTrigger className="w-40" data-testid="filter-country">
              <SelectValue placeholder={t('serviceMvp.browse.filterCountry')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('serviceMvp.browse.allCountries')}</SelectItem>
              {COUNTRY_CODES.map((code) => (
                <SelectItem key={code} value={t(`serviceMvp.countries.${code}`)}>
                  {t(`serviceMvp.countries.${code}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select 
            value={filters.category || "all"} 
            onValueChange={(value) => setFilters({...filters, category: value === "all" ? "" : value})}
          >
            <SelectTrigger className="w-48" data-testid="filter-category">
              <SelectValue placeholder={t('serviceMvp.browse.filterCategory')} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('serviceMvp.browse.allCategories')}</SelectItem>
              {CATEGORY_IDS.map((catId) => (
                <SelectItem key={catId} value={catId}>
                  {t(`serviceMvp.categories.${catId}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : requests.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Search className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">
                {t('serviceMvp.browse.noRequests')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {requests.map((req) => (
              <Card key={req.id} className="hover:shadow-md transition-shadow" data-testid={`request-card-${req.id}`}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary" className="bg-orange-100 text-orange-700">
                      {getCategoryName(req.category)}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      {new Date(req.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <CardDescription className="flex items-center gap-2 mt-2">
                    <MapPin className="h-4 w-4" />
                    {req.city}, {req.country}
                    {req.responses_count > 0 && (
                      <>
                        <span className="mx-2">•</span>
                        <Users className="h-4 w-4" />
                        {req.responses_count} {t('serviceMvp.browse.responses')}
                      </>
                    )}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-sm line-clamp-3">{req.description}</p>
                  <div className="mt-4">
                    <Link to={`/service-request/${req.id}`}>
                      <Button variant="outline" size="sm" data-testid={`view-btn-${req.id}`}>
                        {t('serviceMvp.browse.viewDetails')}
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default ServiceRequestForm;
